import { useEffect, useRef, useState } from "react";
import { app, authentication } from "@microsoft/teams-js";

export interface TeamsAuthState {
  isTeams: boolean;
  token: string | null;
  loading: boolean;
}

/**
 * Detects whether the app is running inside Microsoft Teams and silently
 * acquires an SSO token via the Teams SDK.
 *
 * - Inside Teams: calls `authentication.getAuthToken()` to get a Bearer token
 *   that App Service Easy Auth will validate server-side.
 * - Outside Teams: returns { isTeams: false, token: null } immediately.
 *   The web app relies on Easy Auth's redirect login via session cookie.
 *
 * The token is refreshed on mount only. For v1 the token lifetime (typically
 * 1 hour) is sufficient for a single session; a future iteration can add
 * proactive re-acquisition via a timer.
 */
export function useTeamsAuth(): TeamsAuthState {
  const [isTeams, setIsTeams] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        await app.initialize();
        const context = await app.getContext();
        if (cancelled) return;

        if (context?.app?.host?.name) {
          setIsTeams(true);
          try {
            const t = await authentication.getAuthToken();
            if (!cancelled) {
              tokenRef.current = t;
              setToken(t);
            }
          } catch (err) {
            console.warn("[useTeamsAuth] SSO token acquisition failed:", err);
          }
        }
      } catch {
        // Not inside Teams — app.initialize() rejects when running in a browser.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  return { isTeams, token, loading };
}
